import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// RC49-SRC-003/008/009/012/013 — admin commercial actions, inline forms (no window.prompt)
const PATHS = [["ZELLIGIO_CONTAINER", "Zelligio container"], ["SEA_FREIGHT", "Sea freight"], ["AIR_FREIGHT", "Air freight"]];
const OPT_FIELDS = ["production_timing", "departure_timing", "arrival_window", "freight_range", "merchandise_estimate"];
const SHIP_FIELDS = ["carrier", "method", "departure_date", "arrival_window", "tracking_number", "tracking_url", "reference"];

const Field = ({ id, k, v, set, req }) => (
  <Input value={v || ""} onChange={(e) => set(k, e.target.value)} placeholder={k.replaceAll("_", " ") + (req ? " *" : "")} className="h-9 bg-white" data-testid={`${id}-${k}`} />
);

export function AdminSourcingActions({ r, reload }) {
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(() => Object.fromEntries(PATHS.map(([p]) => [p, { viable: false }])));
  const [quote, setQuote] = useState({ merchandise_usd: "", freight_usd: "", other_charges_usd: "", production_timing: "" });
  const [pay, setPay] = useState({ amount_usd: "", reference: "" });
  const [ship, setShip] = useState({});

  const kinds = [
    ["options", "Present options", ["AVAILABILITY_CONFIRMED", "OPTIONS_PRESENTED", "PRODUCTION_REQUIRED", "QUOTE_REQUIRED", "SOURCING_CONFIRMED"]],
    ["quote", r.status === "FINAL_QUOTE" ? "Revise final quote" : "Issue final quote", ["PREFERENCE_SELECTED", "FINAL_QUOTE"]],
    ["deposit", "Record 50% deposit", ["ACCEPTED_DEPOSIT_DUE"]],
    ["final", "Record final 50%", ["PRODUCTION_COMPLETE_BALANCE_DUE"]],
    ["shipment", "Record shipment", ["FINAL_PAYMENT_RECEIVED", "READY_TO_SHIP"]],
  ].filter(([, , when]) => when.includes(r.status));
  if (!kinds.length) return null;

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setOpen(null); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const num = (v) => (v === "" || v == null ? 0 : parseFloat(v));
  const submit = {
    options: () => run(() => api.post(`/admin/sourcing-requests/${r.id}/options`, {
      options: PATHS.filter(([p]) => opts[p].viable).map(([p]) => ({ path: p, viable: true, ...Object.fromEntries(Object.entries(opts[p]).filter(([k, v]) => k !== "viable" && v)) })),
    }), "Options presented — dealer notified"),
    quote: () => run(() => api.post(`/admin/sourcing-requests/${r.id}/final-quote`, {
      merchandise_usd: num(quote.merchandise_usd), freight_usd: num(quote.freight_usd), other_charges_usd: num(quote.other_charges_usd), production_timing: quote.production_timing || null,
    }), "Final quote issued — dealer notified"),
    deposit: () => run(() => api.post(`/admin/sourcing-requests/${r.id}/payments`, { kind: "deposit", amount_usd: num(pay.amount_usd), reference: pay.reference || null }), "Deposit recorded — production authorized"),
    final: () => run(() => api.post(`/admin/sourcing-requests/${r.id}/payments`, { kind: "final", amount_usd: num(pay.amount_usd), reference: pay.reference || null }), "Final payment recorded — release unlocked"),
    shipment: () => run(() => api.post(`/admin/sourcing-requests/${r.id}/shipment`, { ...Object.fromEntries(Object.entries(ship).filter(([, v]) => v)), carrier: ship.carrier }), "Shipment recorded — dealer notified"),
  };
  const expect = r.final_quote && (open === "deposit" ? r.final_quote.deposit_usd : r.final_quote.balance_usd);

  return (
    <div className="mt-3 border-t border-limestone pt-3" data-testid={`admin-commercial-${r.id}`}>
      <div className="flex flex-wrap gap-2">
        {kinds.map(([kind, label]) => (
          <Button key={kind} size="sm" disabled={busy} onClick={() => setOpen(open === kind ? null : kind)}
            className={open === kind ? "bg-charcoal text-white" : "bg-terracotta text-white hover:bg-terracotta/90"} data-testid={`sourcing-${kind}-${r.id}`}>{label}</Button>
        ))}
      </div>

      {open === "options" && (
        <div className="mt-3 space-y-3" data-testid={`admin-options-form-${r.id}`}>
          <p className="text-xs text-warmgrey">Only verified-viable paths are shown to the dealer. Enter rough estimates — never fabricate timing.</p>
          {PATHS.map(([p, label]) => (
            <div key={p} className="rounded-md border border-limestone p-3">
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal">
                <input type="checkbox" checked={opts[p].viable} onChange={(e) => setOpts({ ...opts, [p]: { ...opts[p], viable: e.target.checked } })} data-testid={`admin-option-viable-${r.id}-${p}`} /> {label} — viable
              </label>
              {opts[p].viable && (
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {OPT_FIELDS.map((k) => <Field key={k} id={`admin-option-${r.id}-${p}`} k={k} v={opts[p][k]} set={(kk, v) => setOpts({ ...opts, [p]: { ...opts[p], [kk]: v } })} />)}
                  {p === "ZELLIGIO_CONTAINER" && <Field id={`admin-option-${r.id}-${p}`} k="container_id" v={opts[p].container_id} set={(kk, v) => setOpts({ ...opts, [p]: { ...opts[p], [kk]: v } })} />}
                </div>
              )}
            </div>
          ))}
          <Button size="sm" disabled={busy || !PATHS.some(([p]) => opts[p].viable)} onClick={submit.options} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid={`admin-options-submit-${r.id}`}>Send options to dealer</Button>
        </div>
      )}

      {open === "quote" && (
        <div className="mt-3 space-y-2" data-testid={`admin-quote-form-${r.id}`}>
          <p className="text-xs text-warmgrey">Path: {r.preferred_path ? r.preferred_path.replaceAll("_", " ") : "—"} · {r.boxes} boxes. Terms: 50% deposit before production, 50% before shipment release.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {["merchandise_usd", "freight_usd", "other_charges_usd", "production_timing"].map((k) => (
              <Field key={k} id={`admin-quote-${r.id}`} k={k} v={quote[k]} set={(kk, v) => setQuote({ ...quote, [kk]: v })} req={k === "merchandise_usd" || k === "freight_usd"} />
            ))}
          </div>
          <p className="text-xs font-semibold text-charcoal" data-testid={`admin-quote-total-${r.id}`}>
            Total ${(num(quote.merchandise_usd) + num(quote.freight_usd) + num(quote.other_charges_usd)).toFixed(2)} · deposit ${((num(quote.merchandise_usd) + num(quote.freight_usd) + num(quote.other_charges_usd)) / 2).toFixed(2)}
          </p>
          <Button size="sm" disabled={busy || quote.merchandise_usd === ""} onClick={submit.quote} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid={`admin-quote-submit-${r.id}`}>Issue quote</Button>
        </div>
      )}

      {(open === "deposit" || open === "final") && (
        <div className="mt-3 space-y-2" data-testid={`admin-payment-form-${r.id}`}>
          <p className="text-xs text-warmgrey">Expected {open === "deposit" ? "deposit" : "balance"}: <strong>${expect?.toFixed(2)}</strong> (TEST mode — recorded, not charged)</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Field id={`admin-payment-${r.id}`} k="amount_usd" v={pay.amount_usd} set={(k, v) => setPay({ ...pay, [k]: v })} req />
            <Field id={`admin-payment-${r.id}`} k="reference" v={pay.reference} set={(k, v) => setPay({ ...pay, [k]: v })} />
          </div>
          <Button size="sm" disabled={busy || !pay.amount_usd} onClick={submit[open]} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid={`admin-payment-submit-${r.id}`}>Record payment</Button>
        </div>
      )}

      {open === "shipment" && (
        <div className="mt-3 space-y-2" data-testid={`admin-shipment-form-${r.id}`}>
          <p className="text-xs text-warmgrey">Set a departure date to mark In transit; leave empty for Ready to ship. Tracking is optional.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {SHIP_FIELDS.map((k) => <Field key={k} id={`admin-shipment-${r.id}`} k={k} v={ship[k]} set={(kk, v) => setShip({ ...ship, [kk]: v })} req={k === "carrier"} />)}
          </div>
          <Button size="sm" disabled={busy || !ship.carrier} onClick={submit.shipment} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid={`admin-shipment-submit-${r.id}`}>Record shipment</Button>
        </div>
      )}
    </div>
  );
}
